'use client';

import { usePathname } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export const LoaderBar = () => {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startLoading = () => {
    setIsLoading(true);
  };

  const stopLoading = () => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setIsLoading(false);
    }, 300);
  };

  const isModifierClick = (event: MouseEvent) => {
    return event.ctrlKey || event.metaKey || event.shiftKey;
  };

  const shouldIgnoreClick = (
    anchor: HTMLAnchorElement | null,
    event: MouseEvent
  ) => {
    if (anchor?.target === '_blank' || anchor?.hasAttribute('download')) {
      return true;
    }

    if (isModifierClick(event)) return true;

    return false;
  };

  const handleClickLink = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');

    if (!anchor || !anchor.href || shouldIgnoreClick(anchor, event)) {
      return;
    }

    const url = new URL(anchor.href, window.location.href);
    const isInternal = url.origin === window.location.origin;
    const isDifferentPage =
      url.href !== window.location.href ||
      url.search !== window.location.search;

    if (isInternal && isDifferentPage) {
      startLoading();
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      startLoading();
      stopLoading();
    }, 0);

    window.addEventListener('beforeunload', startLoading);
    window.addEventListener('popstate', stopLoading);
    document.addEventListener('click', handleClickLink);

    return () => {
      clearTimeout(timeoutId);
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('beforeunload', startLoading);
      window.removeEventListener('popstate', stopLoading);
      document.removeEventListener('click', handleClickLink);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <div
      role="status"
      aria-label="Page Loading"
      className={cn(
        'loading-shimmer loading-shimmer-light pointer-events-none fixed top-0 left-0 z-60 h-1 w-full text-red-500 transition-opacity duration-300 before:opacity-10 before:backdrop-blur-md',
        {
          'loading-shimmer-loading': isLoading,
          'opacity-0': !isLoading,
        }
      )}
      {...createDataAttributes({ test: 'top-loader-bar' })}
    />
  );
};
